/**
 * ============================================================================
 * FiveWhyPanel — Phase 7 frontend (Deviation Management Module).
 * Owner: Phase 7 frontend agent.
 * Purpose: Render a 5-Why investigation tree. The note body is either
 *          newline-delimited (`Why1: ...\nWhy2: ...`) or JSON with a {whys:[]}
 *          shape. Falls back to the raw body if parsing fails.
 *
 * URS rows: DEV-006 (5-Why root-cause analysis).
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DeviationDetail.module.css';

export interface FiveWhyPanelProps {
  body: string;
}

interface Parsed {
  whys: string[];
  rawFallback?: string;
}

function tryParse(body: string): Parsed {
  // Strategy 1: JSON object {whys: [...]}.
  try {
    const obj = JSON.parse(body) as unknown;
    if (
      obj &&
      typeof obj === 'object' &&
      Array.isArray((obj as { whys?: unknown }).whys)
    ) {
      const whys = (obj as { whys: unknown[] }).whys
        .map((x) => (typeof x === 'string' ? x : String(x)))
        .filter((s) => s.trim().length > 0);
      if (whys.length > 0) return { whys };
    }
  } catch {
    /* not JSON, fall through */
  }
  // Strategy 2: newline-delimited with `WhyN: ...` prefix.
  const lines = body
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length > 1 || /^why\s*\d/i.test(lines[0] ?? '')) {
    const stripped = lines.map((l) =>
      l.replace(/^(?:why\s*\d+\s*[:.-]\s*)/i, '').trim(),
    );
    return { whys: stripped };
  }
  return { whys: [], rawFallback: body };
}

export function FiveWhyPanel({ body }: FiveWhyPanelProps) {
  const { t } = useTranslation();
  const parsed = useMemo(() => tryParse(body), [body]);

  if (parsed.whys.length === 0) {
    return (
      <div className={styles.noteBody}>
        {parsed.rawFallback ?? body}
      </div>
    );
  }

  return (
    <div className={styles.fiveWhyPanel}>
      {parsed.whys.map((why, idx) => (
        <div key={idx} className={styles.fiveWhyRow}>
          <span className={styles.fiveWhyLabel}>
            {t('deviation.fiveWhy.label', {
              defaultValue: 'Why {{n}}',
              n: idx + 1,
            })}
          </span>
          <span className={styles.fiveWhyBody}>{why}</span>
        </div>
      ))}
    </div>
  );
}
